import React from "react";
import { Mail, MessageCircle, Instagram, Facebook, Linkedin, Youtube, ArrowRight } from "lucide-react";
import { eventConfig } from "@/config/eventConfig";

const TikTokIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
    <path d="M16.6 5.82a4.28 4.28 0 0 1-1.06-2.82h-2.9v11.6a2.53 2.53 0 1 1-1.8-2.42V9.2a5.43 5.43 0 1 0 4.7 5.38V9.01a7.1 7.1 0 0 0 4.16 1.33V7.44a4.28 4.28 0 0 1-3.1-1.62Z" />
  </svg>
);

const SocialIcon = ({ icon }) => {
  if (icon === "instagram") return <Instagram size={18} />;
  if (icon === "facebook") return <Facebook size={18} />;
  if (icon === "linkedin") return <Linkedin size={18} />;
  if (icon === "youtube") return <Youtube size={18} />;
  if (icon === "tiktok") return <TikTokIcon />;
  return null;
};

const Footer = () => {
  const { brand, legal, footer } = eventConfig;

  const go = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <footer data-testid="footer" className="bg-[#071524] text-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="grid gap-10 lg:gap-12 md:grid-cols-3">
          {/* Column 1: Brand + Connect */}
          <div className="flex flex-col items-center md:items-start text-center md:text-left">
            <img src={brand.logo} alt="Affinity Group logo" className="h-24 w-auto" width="150" height="60" />
            <p className="mt-5 max-w-xs text-sm leading-relaxed text-white/60">{brand.footerBlurb}</p>
            <p className="mt-6 text-sm font-bold tracking-wide text-[#F7B838]">Connect With Us</p>
            <div className="mt-3 flex items-center gap-3" data-testid="footer-social">
              {brand.socials.map((s) => (
                <a
                  key={s.label}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  data-testid={`footer-social-${s.icon}`}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white/70 hover:text-[#071524] hover:bg-[#F7B838] hover:border-[#F7B838] transition-colors"
                >
                  <SocialIcon icon={s.icon} />
                </a>
              ))}
            </div>
            <div className="mt-5 flex flex-col items-center md:items-start gap-2">
              <a href={`mailto:${brand.contactEmail}`} className="inline-flex items-center gap-1.5 text-sm text-white/70 hover:text-white transition-colors">
                <Mail size={14} /> {brand.contactEmail}
              </a>
              <a
                href={`https://wa.me/${brand.whatsappNumber}?text=${encodeURIComponent(brand.whatsappMessage)}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-whatsapp-link"
                className="inline-flex items-center gap-1.5 text-sm text-white/70 hover:text-white transition-colors"
              >
                <MessageCircle size={14} /> {brand.whatsappDisplay}
              </a>
            </div>
          </div>

          {/* Column 2: Quick Links */}
          <div data-testid="footer-quicklinks">
            <h3 className="text-sm font-bold tracking-wide text-[#F7B838]">Quick Links</h3>
            <ul className="mt-4">
              {footer.quickLinks.map((l, i) => (
                <li key={l.href}>
                  <button
                    onClick={() => go(l.href)}
                    data-testid={`footer-quicklink-${i}`}
                    className="group w-full flex items-center justify-between py-3 border-b border-white/10 text-sm text-white/75 hover:text-white transition-colors"
                  >
                    <span>{l.label}</span>
                    <ArrowRight size={15} className="text-white/40 group-hover:text-[#F7B838] group-hover:translate-x-0.5 transition-all" />
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Supporting Partners */}
          <div data-testid="footer-partners">
            <h3 className="text-sm font-bold tracking-wide text-[#F7B838]">Supporting Partners</h3>
            <p className="mt-2 text-sm leading-relaxed text-white/60">{footer.partnersIntro}</p>
            <div className="mt-4 grid grid-cols-2 gap-3">
              {footer.partners.map((p, i) => (
                <div
                  key={p.name}
                  data-testid={`footer-partner-${i}`}
                  className="flex items-center justify-center h-[76px] rounded-xl bg-white border border-white/10 px-4"
                  title={p.name}
                >
                  <img
                    src={p.logo}
                    alt={`${p.name} logo`}
                    loading="lazy"
                    className="max-h-12 max-w-[88%] w-auto object-contain"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="my-8 h-px bg-white/10" />

        {/* Legal links */}
        <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mb-6">
          {legal.footerLinks.map((l, i) => (
            <a key={l.url} href={l.url} data-testid={`footer-link-${i}`} className="text-sm text-white/70 hover:text-white transition-colors">
              {l.label}
            </a>
          ))}
        </div>

        <div className="text-[9px] leading-normal text-white/55">
          <p className="text-white/80 font-semibold text-[15px] mb-3 text-center">{legal.masStatement}</p>
          <div className="md:columns-2 md:gap-8 [&>p]:mb-3 [&>p]:break-inside-avoid">
            {legal.disclaimers.map((d, i) => (
              <p key={d.slice(0, 40)}>
                {d}
                {i === legal.disclaimers.length - 1 ? ` ${legal.masStatement}` : ""}
              </p>
            ))}
          </div>
        </div>

        <div className="mt-8 text-xs text-white/40 text-center">
          © {new Date().getFullYear()} {brand.name}. {brand.tagline}.
        </div>
      </div>
    </footer>
  );
};

export default Footer;
